import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import {
  CreateWarrantyRequest,
  UpdateWarrantyRequest,
  CreateWarrantySuccess,
  UpdateWarrantySuccess,
  FetchContractorsListRequest,
  FetchProjectMembersListRequest,
} from '../../../../../core/projects.actions';
import { contractorsListInfo, projectMembersListInfo } from '../../../../../core/projects.selectors';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';

@Component({
  selector: 'app-warranty-settings-form',
  templateUrl: './warranty-settings-form.component.html',
  styleUrls: ['./warranty-settings-form.component.scss'],
  standalone: false,
})
export class WarrantySettingsFormComponent implements OnInit, OnDestroy {
  @Input() data: any;
  form: FormGroup;
  newUserForm: FormGroup;
  isFormDirty = false;
  reminderDays: number[] = [90, 30];
  newReminderDay: number = null;
  showNewUserForm = false;

  contractorOptions: any[] = [];
  allMemberOptions: any[] = [];
  memberOptions: any[] = [];
  selectedAccounts: any[] = [];
  userSelectForm = new FormGroup({
    selectedUser: new FormControl([]),
  });

  errorMessages: Record<string, string> = {};

  statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
  ];

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private actions$: Actions,
    private projectsService: ProjectsService,
    private appHttpService: AppHttpService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl(''),
      start_date: new FormControl('', Validators.required),
      period_months: new FormControl(24, [Validators.required, Validators.min(1)]),
      status: new FormControl('draft'),
      contractor_id: new FormControl(''),
    });

    this.newUserForm = new FormGroup({
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      phone: new FormControl(''),
    });
  }

  ngOnInit() {
    if (this.data?.id) {
      this.form.patchValue({
        name: this.data.name,
        description: this.data.description,
        start_date: this.data.start_date,
        period_months: this.data.period_months,
        status: this.data.status,
        contractor_id: this.data.contractor_id || this.data.contractor?.id || '',
      });
      this.reminderDays = this.data.reminder_lead_days || [90, 30];
      this.loadWarrantyAccounts();
    }

    // Fetch contractors and members for dropdowns
    this.store.dispatch(FetchContractorsListRequest({ payload: { projectId: this.data.projectId } }));
    this.store.dispatch(FetchProjectMembersListRequest({ payload: { projectId: this.data.projectId } }));

    this.store.select(contractorsListInfo).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      if (data?.list) {
        this.contractorOptions = data.list
          .filter(c => c.prime)
          .map(c => ({
            value: c.id,
            label: c.name || c.poc || c.id,
          }));
        // Auto-select if only one prime contractor and no contractor already set
        if (this.contractorOptions.length === 1 && !this.form.value.contractor_id) {
          this.form.patchValue({ contractor_id: this.contractorOptions[0].value });
        }
        this.cdRef.detectChanges();
      }
    });

    this.store.select(projectMembersListInfo).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      if (data?.list) {
        this.allMemberOptions = data.list
          .filter(m => (m.role_code || m.role || '').startsWith('contractor'))
          .map(m => ({
            value: m.uuid || m.id,
            label: `${m.first_name} ${m.last_name}` + (m.email ? ` (${m.email})` : ''),
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
          }));
        this.updateMemberOptions();
      }
    });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.isFormDirty = this.form.dirty && this.form.valid;
      });

    // When contractor changes, load contractor users as default warranty users
    this.form.get('contractor_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(contractorId => {
        if (contractorId && !this.data?.id) {
          // Only auto-populate for new warranties (not editing existing)
          this.loadContractorUsers(contractorId);
        }
      });

    // When users are selected from the multi-select dropdown, sync to selectedAccounts
    this.userSelectForm.get('selectedUser').valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(vals => {
        if (!Array.isArray(vals)) return;
        // Add any newly selected users
        for (const val of vals) {
          if (val === 'ADD_NEW') continue;
          const member = this.memberOptions.find(m => m.value === val);
          if (member && !this.selectedAccounts.find(a => a.account_id === member.value)) {
            this.selectedAccounts.push({
              account_id: member.value,
              first_name: member.first_name,
              last_name: member.last_name,
              email: member.email,
              role: 'contractor-inspector',
            });
            this.isFormDirty = true;
            this.updateMemberOptions();
          }
        }
      });

    // Close panel on success
    this.actions$.pipe(
      ofType(CreateWarrantySuccess, UpdateWarrantySuccess),
      takeUntil(this.onDestroy),
    ).subscribe(() => {
      this.projectsService.closeRightPanel.next();
    });
  }

  updateMemberOptions() {
    const selectedIds = this.selectedAccounts.map(a => a.account_id);
    this.memberOptions = this.allMemberOptions.filter(m => !selectedIds.includes(m.value));
    this.cdRef.detectChanges();
  }

  checkForErrors(event: any) {
    if (event?.controlName && event?.error) {
      this.errorMessages[event.controlName] = event.error;
    }
  }

  addReminder() {
    if (this.newReminderDay && this.newReminderDay > 0 && !this.reminderDays.includes(this.newReminderDay)) {
      this.reminderDays.push(this.newReminderDay);
      this.reminderDays.sort((a, b) => b - a);
      this.newReminderDay = null;
      this.isFormDirty = true;
    }
  }

  removeReminder(day: number) {
    this.reminderDays = this.reminderDays.filter(d => d !== day);
    this.isFormDirty = true;
  }

  getEndDate(): string {
    const startDate = this.form.get('start_date')?.value;
    const months = this.form.get('period_months')?.value;
    if (!startDate || !months) return '';
    const d = new Date(startDate);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split('T')[0];
  }

  loadWarrantyAccounts() {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.data.projectId}/warranties/${this.data.id}/accounts`
    ).subscribe(resp => {
      if (resp?.data) {
        this.selectedAccounts = resp.data.map(a => ({
          account_id: a.uuid || a.id,
          first_name: a.first_name,
          last_name: a.last_name,
          email: a.email,
          role: a.warranty_role || 'contractor',
        }));
        this.updateMemberOptions();
      }
    });
  }

  loadContractorUsers(contractorId: any) {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.data.projectId}/contractors/${contractorId}/accounts`
    ).subscribe(resp => {
      const accounts = resp?.data || [];
      if (accounts.length) {
        this.selectedAccounts = accounts.map(a => ({
          account_id: a.account_uuid || a.uuid || a.id,
          first_name: a.first_name,
          last_name: a.last_name,
          email: a.email,
          role: a.role || 'contractor',
        }));
        this.updateMemberOptions();
        this.isFormDirty = true;
      }
    });
  }

  addExistingUser(member: any) {
    if (!this.selectedAccounts.find(a => a.account_id === member.value)) {
      this.selectedAccounts.push({
        account_id: member.value,
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        role: 'contractor-inspector',
      });
      this.isFormDirty = true;
    }
  }

  removeUser(account: any) {
    this.selectedAccounts = this.selectedAccounts.filter(a => a.account_id !== account.account_id);
    this.isFormDirty = true;
    this.updateMemberOptions();
  }

  toggleNewUserForm() {
    this.showNewUserForm = !this.showNewUserForm;
  }

  addNewUser() {
    if (!this.newUserForm.valid || !this.data?.id) return;
    const val = this.newUserForm.value;

    // Create account directly via warranty API
    this.appHttpService.postService(
      `/v1/warranty/project/${this.data.projectId}/warranties/${this.data.id}/accounts/create-user`,
      {
        first_name: val.firstName,
        last_name: val.lastName,
        email: val.email,
        ...(val.phone ? { phone: val.phone } : {}),
      },
    ).subscribe((resp: any) => {
      if (resp?.data) {
        this.selectedAccounts.push({
          account_id: resp.data.uuid || resp.data.id,
          first_name: resp.data.first_name,
          last_name: resp.data.last_name,
          email: resp.data.email,
          role: 'contractor-inspector',
        });
        this.updateMemberOptions();
      }
      this.newUserForm.reset();
      this.showNewUserForm = false;
      this.isFormDirty = true;
    });
  }

  submit() {
    if (!this.form.valid) return;
    const formData = {
      ...this.form.value,
      reminder_lead_days: this.reminderDays,
      accounts: this.selectedAccounts.filter(a => !a.isNew).map(a => ({
        account_id: a.account_id,
        role: a.role || 'contractor-inspector',
      })),
    };

    if (this.data?.id) {
      this.store.dispatch(UpdateWarrantyRequest({
        payload: {
          projectId: this.data.projectId,
          warrantyId: this.data.id,
          data: formData,
        },
      }));
    } else {
      this.store.dispatch(CreateWarrantyRequest({
        payload: {
          projectId: this.data.projectId,
          data: formData,
        },
      }));
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
